import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import { MAX_LENGTHS, getRemaining } from './utils/inputValidation';
import { parseServerInputError, isFieldOk } from './utils/inputFormHelpers';
import SessionManager from './components/SessionManager';
import { AuthContext } from './contexts/AuthContext';
import { TRIAL_SIGNUP_CREDITS } from './constants/plans';
import './ProfilePage.css';

function ServiceTierSection({ info }) {
  const navigate = useNavigate();
  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatAmount = (n) =>
    '₩' + (n ?? 0).toLocaleString('ko-KR');

  const daysLabel = (days, status) => {
    if (status === 'pending')   return { text: 'Pending activation', cls: 'tier-days-pending' };
    if (status === 'expired' || days === 0) return { text: 'Membership expired', cls: 'tier-days-expired' };
    if (days <= 30) return { text: `${days} days remaining — expiring soon`, cls: 'tier-days-warning' };
    return { text: `${days} days remaining`, cls: 'tier-days-ok' };
  };

  const categoryLabel = (cat) =>
    cat === 'fgi_partner' ? 'FGI Partner' : 'Standard';

  const categoryClass = (cat) =>
    cat === 'fgi_partner' ? 'tier-category-badge tier-category-fgi' : 'tier-category-badge tier-category-standard';

  const supportBox = (
    <div className="profile-support-box">
      Questions about your plan or billing?{' '}
      Contact our support team at{' '}
      <a href="mailto:hello@whereat.kr">hello@whereat.kr</a>
      <br />
      We're happy to help with membership upgrades, payment questions, or cancellations.
    </div>
  );

  // Still loading
  if (info === undefined) {
    return (
      <section className="profile-section">
        <h2>Service Tier</h2>
        <p className="text-muted">Loading plan info…</p>
      </section>
    );
  }

  // No membership
  if (!info || !info.has_membership) {
    return (
      <section className="profile-section">
        <h2>Service Tier</h2>
        <div className="tier-no-membership">
          {/* KG이니시스 review (T2.7): reports, not credits, and ONE TIME --
              the signup grant never repeats. The number is the shared constant. */}
          <p>You are currently on the <strong>Free Trial</strong> tier ({TRIAL_SIGNUP_CREDITS} analysis reports, one time).</p>
          <p>
            To upgrade, select a plan from our{' '}
            <a href="/#pricing">pricing page</a>{' '}
            or contact us at{' '}
            <a href="mailto:hello@whereat.kr">hello@whereat.kr</a>.
          </p>
        </div>
        <button
          className="tier-manage-btn"
          onClick={() => navigate('/profile/manage-membership')}
        >
          Upgrade Membership
        </button>
        {supportBox}
      </section>
    );
  }

  const { membership, payments } = info;
  const days = daysLabel(membership.days_remaining, membership.status);

  const amountPaid = payments.reduce(
    (sum, p) => (['confirmed', 'paid', 'completed'].includes(p.status) ? sum + (p.amount_krw || 0) : sum), 0
  );
  const amountDue = payments.reduce(
    (sum, p) => (p.status === 'pending' ? sum + (p.amount_krw || 0) : sum), 0
  );

  return (
    <section className="profile-section">
      <h2>Service Tier</h2>

      {/* Membership overview card */}
      <div className="tier-overview-card">
        <div className="tier-overview-header">
          <span className="tier-overview-name">{membership.tier_display_name}</span>
          <span className={`tier-status-badge tier-status-${membership.status}`}>{membership.status}</span>
        </div>
        <div className="tier-overview-dates">
          <span>📅 {formatDate(membership.start_date)}</span>
          <span className="tier-overview-arrow">→</span>
          <span>{formatDate(membership.end_date)}</span>
        </div>
        <div className={`tier-overview-days ${days.cls}`}>{days.text}</div>

        <div className="tier-overview-billing">
          <div className="tier-billing-row">
            <span className="tier-billing-label">Amount Paid</span>
            <span className="tier-billing-value tier-billing-paid">
              {amountPaid > 0 ? formatAmount(amountPaid) : '—'}
            </span>
          </div>
          <div className="tier-billing-row">
            <span className="tier-billing-label">Amount Due</span>
            <span className={`tier-billing-value ${amountDue > 0 ? 'tier-billing-due' : ''}`}>
              {amountDue > 0 ? formatAmount(amountDue) : '—'}
            </span>
          </div>
          <div className="tier-billing-row">
            <span className="tier-billing-label">Membership Status</span>
            <span className={`tier-billing-status tier-billing-status-${membership.status}`}>
              {membership.status === 'active' ? '✅ Active' :
               membership.status === 'pending' ? '⏳ Pending activation' :
               membership.status === 'expired' ? '❌ Expired' : membership.status}
            </span>
          </div>
        </div>

        {membership.status === 'pending' && amountDue > 0 && (
          <div className="tier-due-notice">
            💳 <strong>{formatAmount(amountDue)}</strong> is due before <strong>{formatDate(membership.start_date)}</strong> via wire transfer.
            Contact <a href="mailto:hello@whereat.kr">hello@whereat.kr</a> after payment.
          </div>
        )}
      </div>

      <h3 style={{ fontSize: '0.95rem', margin: '0 0 10px', color: 'var(--text-primary)' }}>Payment History</h3>
      {payments.length === 0 ? (
        <p className="payment-history-empty">No payment records found.</p>
      ) : (
        <table className="payment-history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id}>
                <td>{formatDate(p.payment_date)}</td>
                <td>{formatAmount(p.amount_krw)}</td>
                <td>{p.payment_method ? p.payment_method.replace('_', ' ') : '—'}</td>
                <td>
                  <span className={`payment-status-${p.status}`}>
                    {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button
        className="tier-manage-btn"
        onClick={() => navigate('/profile/manage-membership')}
      >
        Manage Membership
      </button>

      {supportBox}
    </section>
  );
}

export default function ProfilePage() {
  const { refreshUser } = useContext(AuthContext) || {};
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [membershipInfo, setMembershipInfo] = useState(undefined);

  const loadProfile = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/profile');
      if (res.ok) setProfile(await res.json());
    } catch (e) { setError('Failed to load profile'); }
  }, []);

  const loadMembership = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/profile/membership');
      if (res.ok) setMembershipInfo(await res.json());
      else setMembershipInfo(null);
    } catch (e) { setMembershipInfo(null); }
  }, []);

  useEffect(() => { loadProfile(); loadMembership(); }, [loadProfile, loadMembership]);

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        nickname: profile.nickname || '',
        email: profile.email || '',
        company_name: profile.profile?.company_name || '',
        description: profile.profile?.description || '',
      });
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setMessage(''); setError('');
    try {
      const res = await apiFetch('/api/v1/profile', {
        method: 'PUT',
        body: JSON.stringify({
          full_name: form.full_name,
          nickname: form.nickname,
          email: form.email,
          profile: { company_name: form.company_name, description: form.description },
        }),
      });
      if (res.ok) {
        setProfile(await res.json());
        setEditing(false);
        setMessage('Profile updated');
        if (typeof refreshUser === 'function') {
          try { await refreshUser(); } catch { /* non-fatal */ }
        }
      } else {
        const parsed = await parseServerInputError(res, { fallback: 'Failed to update' });
        setError(parsed.message);
      }
    } catch { setError('Failed to save'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    if (pwForm.new_password !== pwForm.confirm) {
      setError('Passwords do not match'); return;
    }
    const newPwResult = isFieldOk(pwForm.new_password, 'password');
    if (!newPwResult.ok) {
      setError(newPwResult.message || 'Invalid new password.'); return;
    }
    try {
      const res = await apiFetch('/api/v1/profile/password', {
        method: 'PUT',
        body: JSON.stringify({ old_password: pwForm.old_password, new_password: pwForm.new_password }),
      });
      if (res.ok) {
        setPwForm({ old_password: '', new_password: '', confirm: '' });
        setMessage('Password changed. You may need to log in again.');
      } else {
        const parsed = await parseServerInputError(res, { fallback: 'Failed to change password' });
        setError(parsed.message);
      }
    } catch { setError('Failed to change password'); }
  };

  const getStrength = (pw) => {
    if (!pw) return { label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { label: 'Weak', color: 'var(--danger)' };
    if (score <= 3) return { label: 'Medium', color: 'var(--warning)' };
    return { label: 'Strong', color: 'var(--success)' };
  };
  const strength = getStrength(pwForm.new_password);

  if (!profile) return <div className="profile-page"><p>Loading...</p></div>;

  return (
    <div className="profile-page">
      <h1>Profile</h1>
      {message && <div className="profile-msg success">{message}</div>}
      {error && <div className="profile-msg error">{error}</div>}

      <ServiceTierSection info={membershipInfo} />

      <section className="profile-section">
        <div className="section-header">
          <h2>Account Info</h2>
          {!editing && <button className="btn-sm" onClick={() => setEditing(true)}>Edit</button>}
        </div>
        {editing ? (
          <div className="edit-form">
            <label>Full Name<input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} maxLength={MAX_LENGTHS.display_name} /></label>
            <label>
              Nickname
              <input
                value={form.nickname}
                maxLength={MAX_LENGTHS.display_name}
                placeholder="Shown on your dashboard. Leave blank to use your full name."
                onChange={e => setForm({...form, nickname: e.target.value})}
              />
            </label>
            <label>Email<input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} maxLength={MAX_LENGTHS.email} autoComplete="email" /></label>
            <label>Company<input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} maxLength={MAX_LENGTHS.short_text} /></label>
            <label>Description<textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} maxLength={MAX_LENGTHS.long_text} />
              {form.description && form.description.length >= Math.floor(MAX_LENGTHS.long_text * 0.8) && (
                <span style={{ fontSize: 11, color: '#6B7280' }}>{getRemaining(form.description, 'long_text')} characters left</span>
              )}
            </label>
            <div className="btn-row">
              <button className="btn-primary" onClick={handleSaveProfile}>Save</button>
              <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="info-grid">
            <div><span className="label">Name</span><span>{profile.full_name}</span></div>
            <div><span className="label">Nickname</span><span>{profile.nickname || '—'}</span></div>
            <div><span className="label">Email</span><span>{profile.email}</span></div>
            <div><span className="label">Role</span><span className={`role-badge ${profile.role}`}>{profile.is_root || profile.can_manage_admins ? 'Sys-Admin' : profile.role === 'admin' ? 'Org-Admin' : profile.role === 'tester' ? 'Sys-Admin' : 'Viewer'}</span></div>
            <div><span className="label">Company</span><span>{profile.profile?.company_name || '—'}</span></div>
            <div><span className="label">Description</span><span>{profile.profile?.description || '—'}</span></div>
          </div>
        )}
      </section>

      <section className="profile-section">
        <h2>Change Password</h2>
        <form onSubmit={handleChangePassword} className="edit-form">
          <label>Current Password<input type="password" value={pwForm.old_password}
            onChange={e => setPwForm({...pwForm, old_password: e.target.value})} maxLength={MAX_LENGTHS.password} autoComplete="current-password" required /></label>
          <label>New Password<input type="password" value={pwForm.new_password}
            onChange={e => setPwForm({...pwForm, new_password: e.target.value})} maxLength={MAX_LENGTHS.password} autoComplete="new-password" required minLength={8} /></label>
          {pwForm.new_password && (
            <div className="strength-bar">
              <div className="strength-fill" style={{ width: strength.label === 'Strong' ? '100%' : strength.label === 'Medium' ? '60%' : '30%', background: strength.color }} />
              <span style={{ color: strength.color }}>{strength.label}</span>
            </div>
          )}
          <label>Confirm Password<input type="password" value={pwForm.confirm}
            onChange={e => setPwForm({...pwForm, confirm: e.target.value})} maxLength={MAX_LENGTHS.password} autoComplete="new-password" required /></label>
          <button type="submit" className="btn-primary">Change Password</button>
        </form>
      </section>

      <section className="profile-section" id="sessions">
        <SessionManager />
      </section>
    </div>
  );
}
